/**
 * i18next configuration for multi-language support.
 * Supports: Japanese (default), English, Vietnamese, Myanmar, Indonesian.
 *
 * Usage:
 * 1. Import this config in app entry point (before rendering)
 * 2. Use useTranslation() hook in components
 * 3. Language changes via i18n.changeLanguage() trigger auto re-render
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from "@/constants/languages";

// Import translation files
import jaCommon from "@/i18n/locales/ja/common.json";
import enCommon from "@/i18n/locales/en/common.json";
import viCommon from "@/i18n/locales/vi/common.json";
import myCommon from "@/i18n/locales/my/common.json";
import idCommon from "@/i18n/locales/id/common.json";
import kmCommon from "@/i18n/locales/km/common.json";
import mnCommon from "@/i18n/locales/mn/common.json";
import neCommon from "@/i18n/locales/ne/common.json";
import thCommon from "@/i18n/locales/th/common.json";
import tlCommon from "@/i18n/locales/tl/common.json";

// Type for translation resources
type TranslationResource = typeof jaCommon;

// Resources object with all translations
const resources: Record<string, { common: TranslationResource }> = {
  ja: { common: jaCommon },
  en: { common: enCommon },
  vi: { common: viCommon },
  my: { common: myCommon },
  id: { common: idCommon },
  km: { common: kmCommon },
  mn: { common: mnCommon },
  ne: { common: neCommon },
  th: { common: thCommon },
  tl: { common: tlCommon },
};

i18n
  // Detect language from localStorage or browser
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: "common",
    ns: ["common"],

    // Language detection options — only check localStorage, fall back to ja (DEFAULT_LANGUAGE)
    detection: {
      order: ["localStorage"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: [],
    },

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React options
    react: {
      useSuspense: false, // Disable suspense for SSR compatibility
    },
  });

export default i18n;
